import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { groq } from 'next-sanity';
import React, { ReactElement } from 'react';

import { LinkButtonRound } from '@components/Buttons/LinkButton';
import { ArticleLayout } from '@components/Layouts';
import { Paragraph } from '@components/Typography';
import { PortableTextBlockRenderer } from '@components/Typography/PortableText';
import { sanityFetch } from '@lib/sanity.fetch';

import type { PortableTextBlock } from '@portabletext/types';

export interface PageData {
  title: string;
  metaTitle: string;
  metaDescription: string;
  slug: string;
  content: PortableTextBlock | PortableTextBlock[];
  _createdAt: string;
  _updatedAt: string;
}

const query = groq`*[_type == "offence" && slug.current == $slug && (!(_id in path("drafts.**")) || $isPreview)]
{
  title,
  metaTitle,
  metaDescription,
  "content": content[]{
    ...,
    markDefs[]{
      ...,
      _type == "notableCase" => {
        "slug": @.item->{
          url
        }.url,
        newTab
      }
    }
  },
  "slug": slug.current,
  _updatedAt,
  _createdAt
}[0]`;

// eslint-disable-next-line no-restricted-syntax
export default function OffencePage({
  preview,
  token,
  queryParams,
  pageData,
}: {
  preview?: boolean;
  token?: string;
  queryParams?: any;
  pageData: PageData;
}): ReactElement {
  return (
    <>
      <Head>
        <title key="title">
          {pageData.metaTitle ? pageData.metaTitle : pageData.title}
        </title>
        {pageData.metaTitle && (
          <meta name="title" content={pageData.metaTitle} key="metatitle" />
        )}
        {pageData.metaDescription && (
          <meta
            name="description"
            content={pageData.metaDescription}
            key="metadescription"
          />
        )}
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}${pageData.slug}`}
        />

        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'Article',
              headline: pageData.title,
              datePublished: pageData._createdAt,
              dateModified: pageData._updatedAt,
              author: [
                {
                  '@type': 'Person',
                  name: 'Pip Monk',
                  url: `${process.env.NEXT_PUBLIC_SERVER_URL}/about`,
                },
              ],
            }),
          }}
        />

        <meta property="og:title" content={pageData.title} key="ogtitle" />
        <meta
          property="og:url"
          content={`${process.env.NEXT_PUBLIC_SERVER_URL}${pageData.slug}`}
          key="ogurl"
        />
        <meta
          property="og:image"
          content={`${process.env.NEXT_PUBLIC_SERVER_URL}/images/Footer logo-12.png`}
          key="ogimage"
        />
        <meta property="og:type" content="website" key="ogtype" />
        <meta
          property="og:description"
          content={pageData.metaDescription}
          key="ogdescription"
        />
      </Head>

      <>
        <h1>{pageData.title}</h1>
        {pageData.content ? (
          <PortableTextBlockRenderer content={pageData.content} />
        ) : (
          <Paragraph>Information coming soon</Paragraph>
        )}
        <LinkButtonRound href="/contact" className="mx-auto my-4">
          contact now
        </LinkButtonRound>
      </>
    </>
  );
}

OffencePage.getLayout = function getLayout(page: ReactElement) {
  return <ArticleLayout>{page}</ArticleLayout>;
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs = await sanityFetch<string[]>({
    draftMode: false,
    query: `//groq
        *[_type == "offence" && !(_id in path("drafts.**"))]{"slug": slug.current}.slug
    `,
  });

  const paths = slugs.map((slug) => ({
    params: {
      offence: slug.slice(slug.indexOf('/', 1) + 1),
    },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({
  params,
  draftMode = false,
}) => {
  const pageData = await sanityFetch<PageData>({
    draftMode,
    query,
    params: {
      slug: '/offences/' + params?.offence?.toString(),
      isPreview: draftMode,
    },
  });

  if (!pageData) {
    return {
      notFound: true,
      revalidate: 3600,
    };
  }

  return {
    props: {
      pageData,
    },
  };
};
